"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowLeft,
  BookOpen,
  Boxes,
  ChevronDown,
  Eye,
  FolderOpen,
  GalleryHorizontalEnd,
  LayoutGrid,
  LogOut,
  Menu,
  Shield,
  UserCircle,
  Users,
  X,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { HubBadge } from "@/components/hub/HubBadge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useAuth } from "@/components/auth/AuthProvider";
import { hasMemberAccess } from "@/lib/authz/policy";
import { canViewShowcase } from "@/lib/showcase/visibility";
import { HubOverlay } from "@/components/hub/HubOverlay";
import { TokenChip } from "@/components/layout/TokenChip";
import { deriveCurrentPlan, planHasAiAccess } from "@/lib/authz/plans";
import type { PaidPlanTier } from "@/lib/authz/planFeatures";
import {
  clearFreePreview,
  enterFreePreview,
  useIsPreviewingFreeUser,
} from "@/lib/home/freePreview";
import {
  clearPlanPreview,
  enterPlanPreview,
  usePreviewAwareAdmin,
  usePreviewPlan,
  useToolAccess,
} from "@/lib/home/planPreview";
// Paid plans offered by the admin "Preview as plan" control, with the
// customer-facing names (PLAN_QUOTAS labels are internal, e.g. "Paint Booth Only").
const PREVIEW_PLANS: { tier: PaidPlanTier; label: string }[] = [
  { tier: "paintbooth", label: "Paint Booth" },
  { tier: "vehicle_dev", label: "Vehicle Developer" },
  { tier: "server_dev", label: "Server Developer" },
  { tier: "all_services", label: "Pro" },
  { tier: "studio", label: "Studio" },
];

const MARKETING_NAV = [
  { label: "Tools", href: "/#tools" },
  { label: "Free tools", href: "/free" },
  { label: "Pricing", href: "/#pricing" },
  { label: "FAQ", href: "/#faq" },
  { label: "Guides", href: "/guides" },
];

export interface AppHeaderBackLink {
  href: string;
  label: string;
}

interface AppHeaderProps {
  /** Optional left-edge back link in the sub-row. */
  back?: AppHeaderBackLink;
  /** Optional right-edge custom controls in the sub-row. */
  actions?: ReactNode;
  /** Optional middle-of-sub-row content (e.g. folder breadcrumb). */
  breadcrumb?: ReactNode;
  /**
   * Admin-only "view as a free user" display toggle (see
   * src/lib/home/freePreview.ts). Re-ANDed against this component's own
   * `isAdmin` from useAuth() below, so a caller passing `true` for a non-admin
   * has no effect — it can only ever change which NAV BRANCH renders for an
   * already-verified admin. It never touches billing/permissions/TokenChip.
   * This prop is now an explicit override on top of the persisted
   * sessionStorage flag (see `useIsPreviewingFreeUser` below) — AppHeader
   * shows the free nav + exit pill whenever *either* is true, so the preview
   * still applies on pages that don't pass this prop at all.
   */
  viewingAsFreeUser?: boolean;
}

/**
 * Unified app header used across every page. Adapts to subscription status:
 * - Non-subscribed (signed out OR signed in without an active subscription): minimal bar with Pricing + Sign in.
 * - Subscribed (or admin): full bar with Tools, My Projects, account, theme toggle.
 *
 * Tool pages render only the top bar — their internal UI establishes which tool
 * you're in, so the older sub-row tool-identity is dropped to avoid a double header.
 * The sub-row still appears for pages that need a back link, breadcrumb, or page-level
 * actions (e.g. My Projects).
 */
export function AppHeader({ back, actions, breadcrumb, viewingAsFreeUser = false }: AppHeaderProps) {
  const { user, isAdmin, billing, permissions: realPermissions, signOut } = useAuth();
  // Previewed plan's permissions while an admin previews a plan (nav gating
  // such as the Assets link); the real ones otherwise.
  const { permissions } = useToolAccess();
  const previewPlan = usePreviewPlan(isAdmin);
  // `isAdmin` (real, server-verified) and "previewing as free" stay two
  // independent variables — previewingFree only feeds the `isSubscriber` nav
  // switch below, it is never assigned back into `isAdmin`. The persisted
  // flag (sessionStorage, via useIsPreviewingFreeUser) is what makes the
  // preview apply on every route; `viewingAsFreeUser` is kept as an explicit
  // prop override for callers (e.g. FreeHome) that already know the answer.
  const persistedPreview = useIsPreviewingFreeUser(isAdmin);
  const previewingFree = isAdmin && (viewingAsFreeUser || persistedPreview);
  // Admin-granted tools count, not just a Seal subscription.
  const realIsSubscriber = hasMemberAccess({ isAdmin, billing, permissions: realPermissions });
  // A plan preview always renders the subscriber nav.
  const isSubscriber = realIsSubscriber && !previewingFree;
  // Customer-facing presentation (plan, AI/token chip, Showcase) follows the
  // previewed plan; the Admin menu / exit pill keep the real flag.
  const { previewTier: previewedTier, displayIsAdmin } = usePreviewAwareAdmin();
  const planTier = previewedTier ?? (previewingFree ? "free" : isAdmin ? "studio" : deriveCurrentPlan(billing?.personalSubscriptionData ?? null));
  const hasAiAccess = planHasAiAccess(planTier, displayIsAdmin && !previewingFree);
  const pathname = usePathname();
  // Don't render the Hub overlay or its trigger on the dedicated /hub page —
  // the page IS the hub, popping the overlay would render the same content twice.
  const onHubPage = pathname === "/hub";
  const [hubOpen, setHubOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleMobileHub = () => {
    setMobileOpen(false);
    setHubOpen(true);
  };
  const handleMobileSignOut = async () => {
    setMobileOpen(false);
    await signOut();
  };
  // Logo always returns to `/`. Subscribers reach the Hub via the explicit "Tools"
  // nav button so the two destinations stay distinct.
  const homeHref = "/";

  const displayName =
    (user?.user_metadata?.full_name as string | undefined)?.trim() || user?.email || "";
  const avatarUrl = (user?.user_metadata?.avatar_url as string | undefined)?.trim() || "";

  const showSubRow = Boolean(back || breadcrumb || actions);

  // Showcase is a public, view-only gallery, so surface it in the marketing nav
  // for signed-out / non-subscribed visitors too. Gated by canViewShowcase so it
  // stays hidden during private preview, and sits right after Tools as a "browse"
  // pairing. Subscribers get the icon'd Showcase link in their own nav branch below.
  const marketingNav = canViewShowcase(displayIsAdmin && !previewingFree)
    ? [MARKETING_NAV[0], { label: "Showcase", href: "/showcase" }, ...MARKETING_NAV.slice(1)]
    : MARKETING_NAV;

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/92 backdrop-blur">
      <div className="flex w-full items-center gap-4 px-6 py-3 sm:px-8">
        {/* Logo — single home affordance. Subscribers go to /hub, everyone else to /. */}
        <Link
          href={homeHref}
          className="flex shrink-0 items-center gap-2.5 transition-opacity hover:opacity-80"
          aria-label="The Workshop"
        >
          <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-xl border border-primary/25 bg-primary/10">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.png" alt="" className="h-6 w-6 object-contain" />
          </span>
          <span className="hidden text-sm font-semibold tracking-tight text-foreground sm:inline">
            The Workshop
          </span>
        </Link>

        {/* Center nav — adapts to subscription status. */}
        <nav className="ml-2 hidden flex-1 items-center gap-1 lg:flex">
          {isSubscriber ? (
            <>
              {!onHubPage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    setHubOpen(true);
                    e.currentTarget.blur();
                  }}
                >
                  <LayoutGrid className="h-4 w-4" />
                  Hub
                </Button>
              )}
              <Button asChild variant="ghost" size="sm">
                <Link href="/my-projects">
                  <FolderOpen className="h-4 w-4" />
                  My Projects
                </Link>
              </Button>
              {permissions.assets ? (
                <Button asChild variant="ghost" size="sm">
                  <Link href="/assets">
                    <Boxes className="h-4 w-4" />
                    Assets
                    {/* Inline and vertically centred on the label; the negative
                        margin pulls it tighter than the button's icon gap. */}
                    <span className="-ml-0.5 inline-flex items-center">
                      <HubBadge kind="new" />
                    </span>
                  </Link>
                </Button>
              ) : null}
              {canViewShowcase(displayIsAdmin && !previewingFree) ? (
                <Button asChild variant="ghost" size="sm">
                  <Link href="/showcase">
                    <GalleryHorizontalEnd className="h-4 w-4" />
                    Showcase
                  </Link>
                </Button>
              ) : null}
              <Button asChild variant="ghost" size="sm">
                <Link href="/guides">
                  <BookOpen className="h-4 w-4" />
                  Guides
                </Link>
              </Button>
            </>
          ) : (
            marketingNav.map((item) => (
              <Button asChild key={item.href} variant="ghost" size="sm">
                <Link href={item.href}>{item.label}</Link>
              </Button>
            ))
          )}
        </nav>
        <div className="flex flex-1 lg:hidden" aria-hidden="true" />

        {/* Right side — desktop has full controls, mobile collapses to a hamburger. */}
        <div className="flex shrink-0 items-center gap-2">
          {!user ? (
            <>
              <Button asChild variant="ghost" size="sm" className="hidden lg:inline-flex">
                <Link href="/auth">Log in</Link>
              </Button>
              <Button asChild variant="premium" size="sm" className="hidden lg:inline-flex">
                <Link href="/#pricing">Get the Workshop</Link>
              </Button>
            </>
          ) : (
            <>
              {!isSubscriber ? (
                <Button asChild variant="ghost" size="sm" className="hidden lg:inline-flex">
                  <Link href="/#pricing">Pricing</Link>
                </Button>
              ) : null}
              {isAdmin ? (
                // Clicking Admin goes to /admin; hovering (or tabbing into it)
                // reveals the preview shortcuts. The `pt-2` wrapper bridges the
                // gap between button and panel so the hover isn't lost crossing it.
                <div className="group relative hidden lg:inline-flex">
                  <Button asChild variant="outline" size="sm">
                    <Link href="/admin" aria-haspopup="menu">
                      <Shield className="h-4 w-4" />
                      Admin
                      <ChevronDown className="h-3.5 w-3.5 opacity-60 transition group-hover:rotate-180 group-focus-within:rotate-180" />
                    </Link>
                  </Button>
                  <div className="invisible absolute right-0 top-full z-50 pt-2 opacity-0 transition duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div
                      role="menu"
                      aria-label="Admin previews"
                      className="min-w-[13rem] rounded-xl border border-border/70 bg-popover p-1 text-popover-foreground shadow-lg"
                    >
                      <Link
                        role="menuitem"
                        href="/?preview=landing"
                        title="Preview the marketing landing page"
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                      >
                        <Eye className="h-4 w-4" />
                        Preview landing
                      </Link>
                      {!previewingFree ? (
                        // Sets the flag, then goes home — rather than linking to
                        // `?preview=free`, which does nothing when you are already
                        // on `/`: only the query string changes, so the header
                        // never remounts and never reads the new param.
                        <Link
                          role="menuitem"
                          href="/"
                          onClick={() => enterFreePreview()}
                          title="Preview the homepage and nav as a free-plan user"
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                        >
                          <Users className="h-4 w-4" />
                          Preview as free
                        </Link>
                      ) : null}
                      <div className="mt-1 border-t border-border/50 px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                        Preview as plan
                      </div>
                      {PREVIEW_PLANS.map((plan) => (
                        <Link
                          key={plan.tier}
                          role="menuitem"
                          href="/"
                          onClick={() => enterPlanPreview(plan.tier)}
                          title={`Preview the homepage and tools as a ${plan.label} subscriber`}
                          aria-current={previewPlan === plan.tier ? "true" : undefined}
                          className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none aria-[current=true]:font-medium"
                        >
                          <Users className="h-4 w-4" />
                          {plan.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
              {hasAiAccess ? (
                <div className="hidden lg:inline-flex">
                  <TokenChip />
                </div>
              ) : null}
              <Link
                href="/account"
                className="hidden items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground lg:flex"
                title="Open account"
              >
                <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-background">
                  {avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
                  ) : (
                    <UserCircle className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </div>
                <span className="max-w-[140px] truncate">{displayName || "Account"}</span>
              </Link>
            </>
          )}
          <div className="hidden lg:inline-flex">
            <ThemeToggle />
          </div>

          {/* Mobile hamburger — collapses every header control into a single sheet below `lg`. */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-[88vw] max-w-sm flex-col gap-0 p-0"
            >
              <SheetTitle className="sr-only">Menu</SheetTitle>

              <div className="flex items-center gap-2.5 border-b border-border/50 px-5 py-4">
                <span className="grid h-9 w-9 place-items-center overflow-hidden rounded-xl border border-primary/25 bg-primary/10">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/logo.png" alt="" className="h-6 w-6 object-contain" />
                </span>
                <span className="text-sm font-semibold tracking-tight text-foreground">
                  The Workshop
                </span>
              </div>

              <nav className="flex-1 overflow-y-auto px-3 py-4">
                <div className="flex flex-col gap-1">
                  {isSubscriber ? (
                    <>
                      {!onHubPage ? (
                        <button
                          type="button"
                          onClick={handleMobileHub}
                          className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                        >
                          <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                          Hub
                        </button>
                      ) : null}
                      <SheetClose asChild>
                        <Link
                          href="/my-projects"
                          className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                        >
                          <FolderOpen className="h-4 w-4 text-muted-foreground" />
                          My Projects
                        </Link>
                      </SheetClose>
                      {permissions.assets ? (
                        <SheetClose asChild>
                          <Link
                            href="/assets"
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                          >
                            <Boxes className="h-4 w-4 text-muted-foreground" />
                            Assets
                            <HubBadge kind="new" />
                          </Link>
                        </SheetClose>
                      ) : null}
                      {canViewShowcase(displayIsAdmin && !previewingFree) ? (
                        <SheetClose asChild>
                          <Link
                            href="/showcase"
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                          >
                            <GalleryHorizontalEnd className="h-4 w-4 text-muted-foreground" />
                            Showcase
                          </Link>
                        </SheetClose>
                      ) : null}
                      <SheetClose asChild>
                        <Link
                          href="/guides"
                          className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                        >
                          <BookOpen className="h-4 w-4 text-muted-foreground" />
                          Guides
                        </Link>
                      </SheetClose>
                    </>
                  ) : (
                    marketingNav.map((item) => (
                      <SheetClose asChild key={item.href}>
                        <Link
                          href={item.href}
                          className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                        >
                          {item.label}
                        </Link>
                      </SheetClose>
                    ))
                  )}
                </div>

                <div className="my-4 border-t border-border/50" />

                <div className="flex flex-col gap-2">
                  {!user ? (
                    <>
                      <SheetClose asChild>
                        <Button asChild variant="outline" size="lg" className="w-full justify-center">
                          <Link href="/auth">Log in</Link>
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button asChild variant="premium" size="lg" className="w-full justify-center">
                          <Link href="/#pricing">Get the Workshop</Link>
                        </Button>
                      </SheetClose>
                    </>
                  ) : (
                    <>
                      <SheetClose asChild>
                        <Link
                          href="/account"
                          className="flex items-center gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-3 text-sm text-foreground transition-colors hover:bg-muted/60"
                        >
                          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-border/60 bg-background">
                            {avatarUrl ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <UserCircle className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <div className="flex min-w-0 flex-col">
                            <span className="truncate text-sm font-medium text-foreground">
                              {displayName || "Account"}
                            </span>
                            <span className="text-xs text-muted-foreground">Manage account</span>
                          </div>
                        </Link>
                      </SheetClose>
                      {!isSubscriber ? (
                        <SheetClose asChild>
                          <Link
                            href="/#pricing"
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                          >
                            Pricing
                          </Link>
                        </SheetClose>
                      ) : null}
                      {isAdmin ? (
                        <SheetClose asChild>
                          <Link
                            href="/?preview=landing"
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                          >
                            <Eye className="h-4 w-4 text-muted-foreground" />
                            Preview landing
                          </Link>
                        </SheetClose>
                      ) : null}
                      {isAdmin && !previewingFree ? (
                        <SheetClose asChild>
                          <Link
                            href="/"
                            onClick={() => enterFreePreview()}
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                          >
                            <Users className="h-4 w-4 text-muted-foreground" />
                            Preview as free
                          </Link>
                        </SheetClose>
                      ) : null}
                      {isAdmin ? (
                        <div className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                          Preview as plan
                        </div>
                      ) : null}
                      {isAdmin
                        ? PREVIEW_PLANS.map((plan) => (
                            <SheetClose asChild key={plan.tier}>
                              <Link
                                href="/"
                                onClick={() => enterPlanPreview(plan.tier)}
                                aria-current={previewPlan === plan.tier ? "true" : undefined}
                                className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60 aria-[current=true]:bg-muted/60"
                              >
                                <Users className="h-4 w-4 text-muted-foreground" />
                                {plan.label}
                              </Link>
                            </SheetClose>
                          ))
                        : null}
                      {isAdmin ? (
                        <SheetClose asChild>
                          <Link
                            href="/admin"
                            className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                          >
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            Admin
                          </Link>
                        </SheetClose>
                      ) : null}
                      <button
                        type="button"
                        onClick={handleMobileSignOut}
                        className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/60"
                      >
                        <LogOut className="h-4 w-4 text-muted-foreground" />
                        Sign out
                      </button>
                    </>
                  )}
                </div>
              </nav>

              <div className="flex items-center justify-between gap-2 border-t border-border/50 px-5 py-4">
                {hasAiAccess ? <TokenChip /> : <span aria-hidden="true" />}
                <ThemeToggle />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {showSubRow ? (
        <div className="border-t border-border/40 bg-background/60 px-6 py-2.5 sm:px-8">
          <div className="flex items-center gap-3">
            {back ? (
              <Link
                href={back.href}
                className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {back.label}
              </Link>
            ) : null}
            {breadcrumb ? <div className="min-w-0 flex-1">{breadcrumb}</div> : <div className="flex-1" />}
            {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
          </div>
        </div>
      ) : null}

      {isSubscriber && !onHubPage ? (
        <HubOverlay open={hubOpen} onOpenChange={setHubOpen} />
      ) : null}

      {/* Escape hatch for the free-user preview — fixed so it stays visible on
          every route (not just wherever this header happens to scroll to),
          on both desktop and the mobile sheet. The Admin nav button above is
          left visible rather than hidden while previewing (it's already
          gated only on `isAdmin`, independent of `previewingFree`), so an
          admin has two ways back: this pill's own Exit, or the Admin link. */}
      {previewingFree ? (
        <PreviewExitPill label="Viewing as a free user" onExit={clearFreePreview} />
      ) : previewPlan ? (
        <PreviewExitPill
          label={`Previewing as ${PREVIEW_PLANS.find((p) => p.tier === previewPlan)?.label ?? "a plan"}`}
          onExit={clearPlanPreview}
        />
      ) : null}
    </header>
  );
}

function PreviewExitPill({ label, onExit }: { label: string; onExit: () => void }) {
  return (
    <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-full border border-border/70 bg-background/95 px-4 py-2 text-sm shadow-lg backdrop-blur">
        <span className="flex items-center gap-2 font-medium text-foreground">
          <Eye className="h-4 w-4 text-accent" />
          {label}
        </span>
        <span className="h-4 w-px bg-border/70" aria-hidden />
        <Link
          href="/admin"
          onClick={onExit}
          className="inline-flex items-center gap-1 text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
          Exit
        </Link>
      </div>
    </div>
  );
}
